/**
 * Objectives Page  —  /prioritization/strategy/objectives
 *
 * Diagram position (from the strategy relationship map):
 *   Strategy → [frames] → Objective → [is root outcome of] → Desired Outcome
 *                                   → [can be specified by] → Goal
 *
 * This page manages Objectives independently of Goals.
 * Downstream: Goals (shown inline, expand), Desired Outcomes (count badge → link).
 * Upstream:   Strategy (shown as context badge on each objective row).
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Target,
  Flag,
  Plus,
  Pencil,
  Trash2,
  ChevronRight,
  ChevronDown,
  TrendingUp,
  Clock,
  Layers,
  TreeDeciduous,
  X,
  Check,
  FileText,
  Link2,
  Search,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { StrategyConnectivityHelpButton } from './StrategyConnectivityDocModal';
import {
  listGoals,
  listObjectives,
  updateObjective,
  deleteObjective,
  type Goal,
  type StrategicObjective,
} from '../../../lib/strategyAlignment';
import { createStrategicObjective as createObjective } from '../../../lib/strategy/createStrategyFlow';
import { useStrategyStore } from '../../../stores/prioritization/strategyStore';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { sendStrategyObjectChanges } from '../../../lib/strategyObjectLinks';
import { ObjectiveWhereItLives } from './ObjectiveWhereItLives';
import { useUserStore } from '../../../stores/prioritization/userStore';

// ============================================================================
// Types
// ============================================================================

interface Strategy { id: string; name: string; description?: string; }

// ============================================================================
// Badge helpers (local copy — no shared coupling)
// ============================================================================

const PRIORITY_COLORS: Record<string, { bg: string; text: string }> = {
  critical: { bg: 'bg-[var(--coral-dim)]',    text: 'text-[var(--coral)]' },
  high:     { bg: 'bg-[var(--mustard-dim)]', text: 'text-[var(--mustard)]' },
  medium:   { bg: 'bg-[var(--slate-dim)]',   text: 'text-[var(--text-sec)]' },
  low:      { bg: 'bg-[var(--hover-tint)]',  text: 'text-[var(--text-sec)]' },
};
const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  active:    { bg: 'bg-[var(--green-dim)]', text: 'text-[var(--green)] dark:text-[var(--text-ter)]' },
  achieved:  { bg: 'bg-[var(--slate-dim)]',  text: 'text-[var(--text-sec)]' },
  deferred:  { bg: 'bg-[var(--mustard-dim)]',   text: 'text-[var(--mustard)]' },
  cancelled: { bg: 'bg-[var(--hover-tint)]',   text: 'text-[var(--text-ter)]' },
};
const HORIZON_ICONS: Record<string, typeof Clock> = {
  quarterly:    Clock,
  annual:       TrendingUp,
  'multi-year': Layers,
};
function PriorityBadge({ value }: { value: string }) {
  const c = PRIORITY_COLORS[value] ?? { bg: 'bg-[var(--hover-tint)]', text: 'text-[var(--text-sec)]' };
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.bg} ${c.text} capitalize`}>{value}</span>;
}
function StatusBadge({ value }: { value: string }) {
  const c = STATUS_COLORS[value] ?? { bg: 'bg-[var(--hover-tint)]', text: 'text-[var(--text-ter)]' };
  return <span className={`text-xs px-2 py-0.5 rounded-full ${c.bg} ${c.text} capitalize`}>{value}</span>;
}
function HorizonBadge({ value }: { value: string }) {
  const Icon = HORIZON_ICONS[value] ?? Clock;
  return <span className="flex items-center gap-1 text-xs text-[var(--text-ter)]"><Icon className="h-3 w-3" />{value}</span>;
}

// ============================================================================
// Form primitives (local)
// ============================================================================

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
        {label}{required && <span className="text-[var(--coral)] ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

const INPUT_CLS  = 'w-full px-3 py-2 text-sm border border-[var(--rule)]  focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 bg-[var(--bg-raised)] text-[var(--text-pri)] placeholder-gray-400 dark:placeholder-gray-500';
const SELECT_CLS = `${INPUT_CLS} appearance-none pr-8`;
const CHEVRON = (
  <svg className="h-4 w-4 pointer-events-none" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
  </svg>
);
function Sel({ value, onChange, children, disabled }: { value: string; onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void; children: React.ReactNode; disabled?: boolean }) {
  return (
    <div className="relative">
      <select value={value} onChange={onChange} disabled={disabled} className={SELECT_CLS}>{children}</select>
      <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-[var(--text-ter)]">{CHEVRON}</span>
    </div>
  );
}

/** Custom strategy picker — same pattern as ValueSourceSelector (fixed backdrop for outside-click). */
function StrategyPicker({ value, onChange, strategies }: {
  value: string;
  onChange: (id: string) => void;
  strategies: Strategy[];
}) {
  const [open, setOpen] = useState(false);
  const selected = strategies.find((s) => s.id === value);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => !strategies.length || setOpen((o) => !o)}
        disabled={strategies.length === 0}
        className={[
          'w-full flex items-center justify-between gap-2  border px-3 py-2 text-sm text-left transition-colors',
          'bg-[var(--bg-raised)] focus:outline-none focus:ring-2 focus:ring-indigo-500',
          open
            ? 'border-[var(--slate)]'
            : 'border-[var(--rule)] hover:border-[var(--slate)]',
          strategies.length === 0 ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
        ].join(' ')}
      >
        <span className={`flex items-center gap-2 min-w-0 truncate ${!selected ? 'text-[var(--text-ter)] italic' : 'text-[var(--text-pri)]'}`}>
          {selected && <Link2 className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />}
          {selected ? selected.name : strategies.length === 0 ? '— No strategies yet —' : '— None —'}
        </span>
        <ChevronDown className="h-4 w-4 flex-shrink-0 text-[var(--text-ter)]" />
      </button>

      {open && (
        <>
          {/* Outside-click backdrop — same pattern as ValueSourceSelector */}
          <div className="fixed inset-0 z-40" aria-hidden="true" onClick={() => setOpen(false)} />

          <div className="absolute z-50 mt-1 w-full border border-[var(--rule)] bg-[var(--bg-raised)] shadow-lg overflow-hidden">
            <ul className="max-h-56 overflow-y-auto divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
              <li>
                <button
                  type="button"
                  onClick={() => { onChange(''); setOpen(false); }}
                  className={[
                    'w-full px-3 py-2 text-sm text-left hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors italic',
                    !value ? 'bg-[var(--surface)] text-[var(--text-ter)]' : 'text-[var(--text-ter)]',
                  ].join(' ')}
                >
                  — None —
                </button>
              </li>
              {strategies.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => { onChange(s.id); setOpen(false); }}
                    className={[
                      'w-full px-3 py-2 text-sm text-left hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/20 transition-colors',
                      value === s.id
                        ? 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 font-medium text-[var(--text-sec)]'
                        : 'text-[var(--text-pri)]',
                    ].join(' ')}
                  >
                    {s.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
function DeleteButton({ onConfirm }: { onConfirm: () => void }) {
  const [confirming, setConfirming] = useState(false);
  if (confirming) return (
    <span className="flex items-center gap-1">
      <button type="button" onClick={() => { onConfirm(); setConfirming(false); }} className="p-1 rounded text-[var(--coral)] hover:bg-[var(--coral-dim)]" title="Confirm"><Check className="h-3.5 w-3.5" /></button>
      <button type="button" onClick={() => setConfirming(false)} className="p-1 rounded text-[var(--text-ter)] hover:bg-[var(--hover-tint)]" title="Cancel"><X className="h-3.5 w-3.5" /></button>
    </span>
  );
  return <button type="button" onClick={() => setConfirming(true)} className="p-1 rounded text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>;
}
function Modal({ title, subtitle, onClose, onSave, saveLabel, saving, canSave, error, children }: {
  title: string; subtitle?: string; onClose: () => void; onSave: () => void;
  saveLabel?: string; saving?: boolean; canSave?: boolean; error?: string | null;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-[var(--bg-raised)] shadow-2xl border border-[var(--rule)] max-w-lg w-full overflow-hidden">
          <div className="flex items-start justify-between px-6 py-4 border-b border-[var(--rule)]">
            <div>
              <h3 className="text-base font-semibold text-[var(--text-pri)]">{title}</h3>
              {subtitle && <p className="text-xs text-[var(--text-ter)] mt-0.5">{subtitle}</p>}
            </div>
            <button type="button" onClick={onClose} className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]"><X className="h-4 w-4" /></button>
          </div>
          <div className="px-6 py-5 space-y-4">
            {error && <div className="border border-[var(--coral)] dark:border-[var(--coral)] bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 px-3 py-2 text-sm text-[var(--coral)] dark:text-[var(--coral)]">{error}</div>}
            {children}
          </div>
          <div className="flex justify-end gap-2 px-6 py-4 border-t border-[var(--rule)] bg-[var(--surface)]/50 dark:bg-[var(--bg-raised)]/30">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-[var(--text-sec)] hover:text-[var(--text-pri)] dark:hover:text-[var(--text-ter)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors">Cancel</button>
            <button type="button" onClick={onSave} disabled={saving || canSave === false} className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {saving ? 'Saving…' : (saveLabel ?? 'Save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Objective modal
// ============================================================================

type ObjForm = {
  name: string; description: string; strategyId: string;
  timeHorizon: 'quarterly' | 'annual' | 'multi-year';
  priority: 'critical' | 'high' | 'medium' | 'low';
  status: 'active' | 'achieved' | 'deferred' | 'cancelled';
};

function ObjectiveModal({ initial, strategies, defaultStrategyId, onClose, onSaved, workspaceId, userId }: {
  initial?: StrategicObjective; strategies: Strategy[]; defaultStrategyId?: string;
  onClose: () => void; onSaved: () => void; workspaceId: string; userId: string;
}) {
  const [form, setForm] = useState<ObjForm>({
    name:        initial?.name        ?? '',
    description: initial?.description ?? '',
    strategyId:  initial?.strategyId  ?? defaultStrategyId ?? '',
    timeHorizon: initial?.timeHorizon ?? 'quarterly',
    priority:    initial?.priority    ?? 'high',
    status:      initial?.status      ?? 'active',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState<string | null>(null);

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Name is required.'); return; }
    setSaving(true); setError(null);
    try {
      if (initial) {
        await updateObjective(initial.id, { name: form.name.trim(), description: form.description.trim(), strategyId: form.strategyId || undefined, timeHorizon: form.timeHorizon, priority: form.priority, status: form.status });
        // Linked to a record elsewhere: send the change now, not at the next 15-minute run.
        void sendStrategyObjectChanges(workspaceId, 'objective', initial.id).catch(() => undefined);
      } else {
        await createObjective({ name: form.name.trim(), description: form.description.trim(), strategyId: form.strategyId || undefined, timeHorizon: form.timeHorizon, priority: form.priority, status: form.status, workspaceId, createdBy: userId }, userId);
      }
      onSaved();
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={initial ? 'Edit Objective' : 'New Objective'} subtitle="A qualitative target — what achieving the strategy looks like. Goals live beneath objectives." onClose={onClose} onSave={handleSave} saving={saving} canSave={!!form.name.trim()} error={error} saveLabel={initial ? 'Save Changes' : 'Create Objective'}>
      <Field label="Name" required>
        <input autoFocus type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Become the default tool for outcome-driven teams" className={INPUT_CLS} />
      </Field>
      <Field label="Description">
        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="What does success look like for this objective?" className={`${INPUT_CLS} resize-none`} />
      </Field>
      {/* ↑ Upstream: Strategy */}
      <Field label="Strategy">
        <StrategyPicker
          value={form.strategyId}
          onChange={(id) => setForm({ ...form, strategyId: id })}
          strategies={strategies}
        />
        {strategies.length === 0 && (
          <p className="mt-1 text-xs text-[var(--text-ter)]">
            Create a strategy first at <Link to="/prioritization/strategy" className="underline hover:text-[var(--text-ter)]">Strategies</Link>.
          </p>
        )}
      </Field>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Time horizon">
          <Sel value={form.timeHorizon} onChange={(e) => setForm({ ...form, timeHorizon: e.target.value as ObjForm['timeHorizon'] })}>
            <option value="quarterly">Quarterly</option>
            <option value="annual">Annual</option>
            <option value="multi-year">Multi-year</option>
          </Sel>
        </Field>
        <Field label="Priority">
          <Sel value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value as ObjForm['priority'] })}>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </Sel>
        </Field>
        <Field label="Status">
          <Sel value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as ObjForm['status'] })}>
            <option value="active">Active</option>
            <option value="achieved">Achieved</option>
            <option value="deferred">Deferred</option>
            <option value="cancelled">Cancelled</option>
          </Sel>
        </Field>
      </div>
    </Modal>
  );
}

// ============================================================================
// Objectives list
// ============================================================================

export default function ObjectivesPage() {
  const userId             = useUserStore((s) => s.userId) ?? '';
  const currentWorkspaceId = useTenantStore((s) => s.currentWorkspaceId) ?? '';

  const strategyMap     = useStrategyStore((s) => s.strategies);
  const fetchStrategies = useStrategyStore((s) => s.fetchFromSupabase);
  const strategies      = useMemo<Strategy[]>(
    () => Object.values(strategyMap).map((s) => ({ id: s.id, name: s.name, description: s.northStarGoal ?? undefined })),
    [strategyMap],
  );

  const [searchParams]     = useSearchParams();
  const [filterStrategyId, setFilterStrategyId] = useState(searchParams.get('strategyId') ?? '');
  const [objectiveSearch, setObjectiveSearch] = useState('');
  const [objectives,  setObjectives]  = useState<StrategicObjective[]>([]);
  const [goals,       setGoals]       = useState<Goal[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [modal,       setModal]       = useState<'create' | StrategicObjective | null>(null);
  const [expanded,    setExpanded]    = useState<Set<string>>(new Set());

  const loadAll = useCallback(async () => {
    if (!currentWorkspaceId) return;
    const [o, g] = await Promise.all([
      listObjectives(currentWorkspaceId),
      listGoals(currentWorkspaceId),
    ]);
    setObjectives(o);
    setGoals(g);
  }, [currentWorkspaceId]);

  useEffect(() => {
    if (!currentWorkspaceId) { setLoading(false); return; }
    setLoading(true);
    Promise.all([fetchStrategies(), loadAll()]).finally(() => setLoading(false));
  }, [currentWorkspaceId, loadAll, fetchStrategies]);

  const toggle = (id: string) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const visible = useMemo(() => {
    let list = filterStrategyId
      ? objectives.filter((o) => o.strategyId === filterStrategyId)
      : objectives;
    const q = objectiveSearch.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (o) =>
          o.name.toLowerCase().includes(q) ||
          (o.description?.toLowerCase().includes(q))
      );
    }
    return list;
  }, [objectives, filterStrategyId, objectiveSearch]);

  const hasFilters = Boolean(filterStrategyId || objectiveSearch.trim());

  if (!currentWorkspaceId) return <div className="p-8 text-center text-[var(--text-ter)] border border-[var(--rule)] bg-[var(--surface)]">Select a workspace to manage objectives.</div>;
  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>;

  const filterName = strategies.find((s) => s.id === filterStrategyId)?.name;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Target className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title="Objectives"
        subtitle={filterName ? `Filtered to strategy: ${filterName}` : 'Qualitative targets — what achieving the strategy looks like. Goals and Desired Outcomes live beneath each Objective.'}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <StrategyConnectivityHelpButton context="objectives" />
            {hasFilters && (
              <button
                type="button"
                onClick={() => {
                  setFilterStrategyId('');
                  setObjectiveSearch('');
                }}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)]"
              >
                <X className="h-3.5 w-3.5" /> Clear filters
              </button>
            )}
            <Link to="/prioritization/strategy" className="flex items-center gap-1.5 px-3 py-2 text-sm text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)]">
              <TreeDeciduous className="h-3.5 w-3.5" /> Strategies
            </Link>
            <button type="button" onClick={() => setModal('create')} className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary-600 text-white text-sm hover:bg-primary-700">
              <Plus className="h-4 w-4" /> New Objective
            </button>
          </div>
        }
      />

      {/* Strategy dropdown + search — avoids N strategy pills; search helps large objective lists */}
      {(strategies.length > 0 || objectives.length > 0) && (
        <div className="border border-[var(--rule)] bg-[var(--surface)]/80 dark:bg-[var(--bg)]/40 px-4 py-4 space-y-3">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
            {strategies.length > 0 ? (
              <div className="flex-1 min-w-0">
                <label htmlFor="objectives-filter-strategy" className="block text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1.5">
                  Strategy
                </label>
                <div className="relative">
                  <select
                    id="objectives-filter-strategy"
                    value={filterStrategyId}
                    onChange={(e) => setFilterStrategyId(e.target.value)}
                    className={`${SELECT_CLS} w-full lg:max-w-md`}
                  >
                    <option value="">
                      All strategies ({objectives.length} {objectives.length === 1 ? 'objective' : 'objectives'})
                    </option>
                    {strategies.map((s) => {
                      const c = objectives.filter((o) => o.strategyId === s.id).length;
                      return (
                        <option key={s.id} value={s.id}>
                          {s.name} ({c} {c === 1 ? 'objective' : 'objectives'})
                        </option>
                      );
                    })}
                  </select>
                  <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-[var(--text-ter)]">{CHEVRON}</span>
                </div>
                <p className="mt-1.5 text-xs text-[var(--text-ter)]">
                  {strategies.length} {strategies.length === 1 ? 'strategy' : 'strategies'} in this workspace — pick one to focus, or leave on &ldquo;All strategies&rdquo;.
                </p>
              </div>
            ) : (
              <div className="flex-1 min-w-0 lg:max-w-md">
                <p className="text-xs text-[var(--text-ter)]">
                  No classic strategies in this workspace yet. You can still search objectives below.
                </p>
              </div>
            )}
            <div className={`flex-1 min-w-0 ${strategies.length === 0 ? 'w-full' : ''}`}>
              <label htmlFor="objectives-search" className="block text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1.5">
                Search objectives
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--text-ter)]" />
                <input
                  id="objectives-search"
                  type="search"
                  value={objectiveSearch}
                  onChange={(e) => setObjectiveSearch(e.target.value)}
                  placeholder="Name or description…"
                  autoComplete="off"
                  className={`${INPUT_CLS} pl-9 w-full ${strategies.length > 0 ? 'lg:max-w-md' : ''}`}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {objectives.length === 0 ? (
        <div className="text-center py-16 text-[var(--text-ter)]">
          <Target className="h-12 w-12 mx-auto text-[var(--text-ter)] mb-3" />
          <p className="font-medium text-[var(--text-sec)]">No objectives yet</p>
          <p className="text-sm mt-1 max-w-sm mx-auto">Objectives define what achieving a strategy looks like — qualitative, aspirational targets. Goals beneath them provide the measurable signal.</p>
          <button type="button" onClick={() => setModal('create')} className="mt-4 inline-flex items-center gap-1.5 px-3 py-2 bg-primary-600 text-white text-sm hover:bg-primary-700">
            <Plus className="h-4 w-4" /> New Objective
          </button>
        </div>
      ) : visible.length === 0 ? (
        <div className="border border-dashed border-[var(--rule)] bg-[var(--surface)]/50 dark:bg-[var(--bg)]/30 px-6 py-12 text-center">
          <Search className="h-10 w-10 mx-auto text-[var(--text-ter)] mb-3" />
          <p className="font-medium text-[var(--text-sec)]">No objectives match</p>
          <p className="text-sm text-[var(--text-ter)] mt-1 max-w-md mx-auto">
            {hasFilters
              ? 'Try clearing the strategy filter or search. Objectives may exist outside this view.'
              : 'Adjust your filters to see objectives.'}
          </p>
          {hasFilters && (
            <button
              type="button"
              onClick={() => {
                setFilterStrategyId('');
                setObjectiveSearch('');
              }}
              className="mt-4 inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-primary-700 dark:text-primary-400 border border-primary-200 dark:border-primary-800 hover:bg-primary-50 dark:hover:bg-primary-900/20"
            >
              <X className="h-3.5 w-3.5" /> Clear filters
            </button>
          )}
        </div>
      ) : (
        <>
          {(filterStrategyId || objectiveSearch.trim()) && (
            <p className="text-xs text-[var(--text-ter)]">
              Showing <span className="font-medium text-[var(--text-sec)]">{visible.length}</span> of{' '}
              {objectives.length} objective{objectives.length === 1 ? '' : 's'}
              {filterName ? (
                <>
                  {' '}
                  · strategy: <span className="font-medium text-[var(--text-sec)]">{filterName}</span>
                </>
              ) : null}
              {objectiveSearch.trim() ? (
                <>
                  {' '}
                  · search: <span className="font-medium text-[var(--text-sec)]">&ldquo;{objectiveSearch.trim()}&rdquo;</span>
                </>
              ) : null}
            </p>
          )}
        <div className="space-y-2">
          {visible.map((obj) => {
            const strategy   = strategies.find((s) => s.id === obj.strategyId);
            const childGoals = goals.filter((g) => g.objectiveId === obj.id);
            const isExpanded = expanded.has(obj.id);

            return (
              <div key={obj.id} className="border border-[var(--rule)] overflow-hidden bg-[var(--bg-raised)] shadow-sm">
                {/* Objective row */}
                <div className="flex flex-wrap items-center gap-3 px-4 py-3 hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)]/60 transition-colors group">
                  <button type="button" onClick={() => toggle(obj.id)} className="p-0.5 hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-lift)] rounded flex-shrink-0">
                    {isExpanded ? <ChevronDown className="h-3.5 w-3.5 text-[var(--text-ter)]" /> : <ChevronRight className="h-3.5 w-3.5 text-[var(--text-ter)]" />}
                  </button>
                  <Target className="h-4 w-4 text-primary-500 flex-shrink-0" />
                  {/* Grows to fill the row; on a phone the badges wrap below it */}
                  <div className="min-w-0 flex-[1_1_12rem]">
                    <p className="text-sm font-semibold text-[var(--text-pri)] truncate">{obj.name}</p>
                    {obj.description && <p className="text-xs text-[var(--text-ter)] truncate">{obj.description}</p>}
                    {/* ↑ Upstream: Strategy */}
                    {strategy && (
                      <p className="text-xs text-[var(--text-ter)] mt-0.5 flex items-center gap-1">
                        <Target className="h-3 w-3 text-primary-300" />
                        <Link to={`/prioritization/strategy/${strategy.id}`} className="hover:text-primary-600 transition-colors">{strategy.name}</Link>
                      </p>
                    )}
                  </div>
                  <div className="ml-auto flex flex-wrap items-center gap-2">
                    <PriorityBadge value={obj.priority} />
                    <StatusBadge value={obj.status} />
                    <HorizonBadge value={obj.timeHorizon} />
                    {/* ↓ Downstream counts */}
                    <span className="text-xs text-[var(--text-ter)] min-w-[3ch] text-right">{childGoals.length} goal{childGoals.length !== 1 ? 's' : ''}</span>
                    {/* ↓ Downstream: link to Desired Outcomes filtered by this objective */}
                    <Link
                      to={`/prioritization/desired-outcomes?objectiveId=${obj.id}`}
                      className="flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                      title="View Desired Outcomes for this Objective"
                    >
                      <FileText className="h-3.5 w-3.5" />
                    </Link>
                    <button type="button" onClick={() => setModal(obj)} className="p-1 rounded text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] opacity-0 group-hover:opacity-100 transition-all" title="Edit">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <DeleteButton onConfirm={async () => { await deleteObjective(obj.id); loadAll(); }} />
                  </div>
                </div>

                {/* ↓ Downstream: Goals under this objective */}
                {isExpanded && (
                  <div className="border-t border-[var(--rule)]">
                    {childGoals.length === 0 ? (
                      <div className="flex items-center gap-3 px-10 py-2.5">
                        <p className="text-xs text-[var(--text-ter)] italic flex-1">No goals under this objective.</p>
                        <Link to={`/prioritization/strategy/goals?objectiveId=${obj.id}`} className="text-xs text-primary-600 hover:underline">+ Add goal</Link>
                      </div>
                    ) : (
                      <div className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
                        {childGoals.map((g) => (
                          <div key={g.id} className="flex flex-wrap items-center gap-3 pl-10 pr-4 py-2.5 hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)]/40">
                            <Flag className="h-3.5 w-3.5 text-[var(--mustard)] flex-shrink-0" />
                            <p className="min-w-0 flex-[1_1_10rem] text-sm text-[var(--text-pri)] truncate">{g.name}</p>
                            {g.metricName && (
                              <span className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] dark:bg-[var(--green)]/30 px-1.5 py-0.5 rounded">{g.metricName}</span>
                            )}
                            <PriorityBadge value={g.priority} />
                            <StatusBadge value={g.status} />
                          </div>
                        ))}
                      </div>
                    )}
                    <ObjectiveWhereItLives workspaceId={currentWorkspaceId} objective={obj} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </>
      )}

      {(modal === 'create' || (modal && typeof modal === 'object')) && (
        <ObjectiveModal
          initial={modal !== 'create' ? (modal as StrategicObjective) : undefined}
          strategies={strategies}
          defaultStrategyId={filterStrategyId || undefined}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); loadAll(); }}
          workspaceId={currentWorkspaceId}
          userId={userId}
        />
      )}
    </div>
  );
}
